import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router";
import { motion } from "motion/react";
import { Mail, Lock, Eye, EyeOff, LogIn } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AuthApiError, requestAccountSetupEmail } from "../services/auth";
import { AuthLoadingScreen } from "../components/AuthLoadingScreen";
import { PasswordChangePrompt } from "../components/PasswordChangePrompt";
import { Seo } from "../components/Seo";
import logo from "@/assets/logo.svg";

export function Login() {
  const location = useLocation();
  const initialEmail = new URLSearchParams(location.search).get("email") || "";
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [showEmailSetupRequest, setShowEmailSetupRequest] = useState(
    () => new URLSearchParams(location.search).get("setup") === "1",
  );
  const [setupRequestEmail, setSetupRequestEmail] = useState(initialEmail);
  const [setupRequestMessage, setSetupRequestMessage] = useState("");
  const [setupRequestError, setSetupRequestError] = useState("");
  const [isSendingSetupRequest, setIsSendingSetupRequest] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, isInitializing, user } = useAuth();
  const navigate = useNavigate();
  const nextParam = new URLSearchParams(location.search).get("next");
  const nextPath =
    nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")
      ? nextParam
      : null;
  const defaultTargetPath = user?.role === "admin" ? "/admin" : "/dashboard";
  const targetPath =
    user?.role === "admin" && nextPath?.startsWith("/dashboard") ? defaultTargetPath : nextPath;

  useEffect(() => {
    if (isInitializing || !isAuthenticated || !user || showPasswordSetup) {
      return;
    }

    navigate(targetPath || defaultTargetPath, { replace: true });
  }, [
    defaultTargetPath,
    isAuthenticated,
    isInitializing,
    navigate,
    showPasswordSetup,
    targetPath,
    user,
  ]);

  useEffect(() => {
    const nextEmail = new URLSearchParams(location.search).get("email") || "";
    const shouldShowSetupRequest = new URLSearchParams(location.search).get("setup") === "1";
    setEmail(nextEmail);
    setSetupRequestEmail(nextEmail);
    setShowEmailSetupRequest(shouldShowSetupRequest);
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const authenticatedUser = await login(email, password);
      if (authenticatedUser.mustChangePassword) {
        setShowPasswordSetup(true);
        return;
      }

      const authenticatedDefaultTarget = authenticatedUser.role === "admin" ? "/admin" : "/dashboard";
      const authenticatedTarget =
        authenticatedUser.role === "admin" && nextPath?.startsWith("/dashboard")
          ? authenticatedDefaultTarget
          : nextPath;

      navigate(authenticatedTarget || authenticatedDefaultTarget);
    } catch (err) {
      if (err instanceof AuthApiError) {
        setError(err.message);
      } else {
        setError("Ein Fehler ist beim Anmelden aufgetreten");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestSetupLink = async (event: React.FormEvent) => {
    event.preventDefault();
    setSetupRequestError("");
    setSetupRequestMessage("");
    setIsSendingSetupRequest(true);

    try {
      const message = await requestAccountSetupEmail(setupRequestEmail);
      setSetupRequestMessage(message);
    } catch (requestError) {
      setSetupRequestError(
        requestError instanceof AuthApiError
          ? requestError.message
          : "Der Link konnte nicht angefordert werden",
      );
    } finally {
      setIsSendingSetupRequest(false);
    }
  };

  if (isInitializing) {
    return <AuthLoadingScreen message="Login-Status wird geladen..." />;
  }

  if (showPasswordSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Seo
          title="Passwort festlegen"
          description="Geschützter Bereich zur Einrichtung Ihres Zugangs."
          path="/login"
          noIndex
        />
        <div className="w-full max-w-md">
          <Link to="/" className="flex justify-center mb-8">
            <img src={logo} alt="mutzur" className="h-6" />
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <PasswordChangePrompt
              title="Eigenes Passwort festlegen"
              description="Sie haben sich mit dem Portal-Passwort angemeldet. Bitte vergeben Sie jetzt aus Sicherheitsgründen Ihr eigenes Passwort."
              submitLabel="Passwort speichern und weiter"
              secondaryActionLabel="Später im Dashboard"
              onSecondaryAction={() => navigate(targetPath || defaultTargetPath, { replace: true })}
              onSuccess={() => navigate(targetPath || defaultTargetPath, { replace: true })}
            />
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Seo
        title="Login"
        description="Login zum Kundenbereich von mutzur."
        path="/login"
        noIndex
      />
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex justify-center mb-8">
          <img src={logo} alt="mutzur" className="h-6" />
        </Link>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">Willkommen zurück</h1>
            <p className="text-gray-600">
              Melden Sie sich in Ihrem Kundenbereich an
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium mb-2 text-gray-700"
              >
                E-Mail-Adresse
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="ihre@email.de"
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium mb-2 text-gray-700"
              >
                Passwort
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                "Wird angemeldet..."
              ) : (
                <>
                  <LogIn size={20} />
                  Anmelden
                </>
              )}
            </button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-3">
            <button
              type="button"
              onClick={() => {
                setShowEmailSetupRequest((current) => !current);
                setSetupRequestError("");
                setSetupRequestMessage("");
              }}
              className="block w-full text-sm text-gray-600 hover:text-black"
            >
              Noch kein Passwort vergeben? Link per E-Mail anfordern
            </button>
            <Link
              to="/fragebogen"
              className="block text-sm text-gray-600 hover:text-black"
            >
              Noch kein Kunde? Jetzt Website erstellen
            </Link>
            <Link to="/" className="block text-sm text-gray-600 hover:text-black">
              Zurück zur Website
            </Link>
          </div>

          {showEmailSetupRequest ? (
            <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-5">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Passwort-Link anfordern</h2>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">
                  Wenn zu Ihrer E-Mail bereits eine Bestellung vorliegt, Sie aber noch kein
                  Passwort vergeben haben, senden wir Ihnen einen Aktivierungslink.
                </p>
              </div>

              <form onSubmit={handleRequestSetupLink} className="space-y-4">
                <div>
                  <label
                    htmlFor="setup-request-email"
                    className="block text-sm font-medium mb-2 text-gray-700"
                  >
                    E-Mail-Adresse
                  </label>
                  <div className="relative">
                    <Mail
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      id="setup-request-email"
                      type="email"
                      value={setupRequestEmail}
                      onChange={(event) => setSetupRequestEmail(event.target.value)}
                      required
                      placeholder="ihre@email.de"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
                    />
                  </div>
                </div>

                {setupRequestError ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    {setupRequestError}
                  </div>
                ) : null}

                {setupRequestMessage ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    {setupRequestMessage}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={isSendingSetupRequest}
                  className="w-full rounded-lg bg-black px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSendingSetupRequest ? "Link wird gesendet..." : "Link per E-Mail senden"}
                </button>
              </form>
            </div>
          ) : null}
        </motion.div>
      </div>
    </div>
  );
}
